import React, { useMemo, useState } from "react";
import { planQuestionType } from "../../settings/shared/constants";
import { formatTimestamp } from "./chatUtils";
import {
  areAllRequiredPlanQuestionsAnswered,
  countAnsweredPlanQuestions,
  getFirstUnansweredPlanIndex,
  getOptionLabel,
  isPlanQuestionAnswered,
} from "./planUtils";

const isSelectedValue = (selected, value) => {
  if (Array.isArray(selected)) return selected.map(String).includes(String(value));
  return selected != null && String(selected) === String(value);
};

const OptionRow = ({ option, selected, multiple, disabled, onSelect }) => (
  <button
    type="button"
    className={`sa-plan-option ${selected ? "is-selected" : ""} ${disabled ? "is-disabled" : ""}`}
    onClick={() => !disabled && onSelect(option.value)}
    disabled={disabled}
    aria-pressed={selected}
    role={multiple ? "checkbox" : "radio"}
    aria-checked={selected}
  >
    <span className={`sa-plan-option-mark ${multiple ? "is-check" : "is-radio"}`} aria-hidden="true">
      {multiple ? (
        selected ? <i className="fa-solid fa-check" /> : null
      ) : (
        selected ? <span className="sa-plan-option-dot" /> : null
      )}
    </span>
    <span className="sa-plan-option-copy">
      <span className="sa-plan-option-label">{option.label}</span>
      {option.description ? (
        <span className="sa-plan-option-desc">{option.description}</span>
      ) : null}
    </span>
  </button>
);

const SelectQuestion = ({ question, value, disabled, onChange }) => {
  const multiple = question.type === planQuestionType.MULTI_SELECT;
  if (!(question.options || []).length) {
    return (
      <p className="sa-plan-unsupported-note">No options were provided for this question.</p>
    );
  }
  const handleSelect = (optionValue) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      const exists = current.map(String).includes(String(optionValue));
      onChange(exists ? current.filter((item) => String(item) !== String(optionValue)) : [...current, optionValue]);
      return;
    }
    onChange(optionValue);
  };

  return (
    <div
      className="sa-plan-options"
      role={multiple ? "group" : "radiogroup"}
      aria-label={question.question}
    >
      {(question.options || []).map((option) => (
        <OptionRow
          key={option.value}
          option={option}
          selected={isSelectedValue(value, option.value)}
          multiple={multiple}
          disabled={disabled}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
};

const TextQuestion = ({ question, value, disabled, onChange }) => (
  <textarea
    className="sa-plan-text-input"
    value={value || ""}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
    placeholder={question.description || "Type your answer"}
    rows={3}
    aria-label={question.question}
  />
);

const UnsupportedQuestion = ({ question, value, disabled, onChange }) => (
  <div className="sa-plan-unsupported">
    <p className="sa-plan-unsupported-note">
      This question type is not yet interactive. You can still type an answer.
    </p>
    <TextQuestion question={question} value={typeof value === "string" ? value : ""} disabled={disabled} onChange={onChange} />
  </div>
);

const QUESTION_RENDERERS = {
  [planQuestionType.SINGLE_SELECT]: SelectQuestion,
  [planQuestionType.MULTI_SELECT]: SelectQuestion,
  [planQuestionType.TEXT]: TextQuestion,
};

const defaultAnswerFor = (question) =>
  question?.type === planQuestionType.MULTI_SELECT ? [] : "";

const PlanQuestionCard = ({ message, onContinue, onProgress }) => {
  const questions = message?.planQuestions || [];
  const submitted = message?.planStatus === "submitted";
  const [answers, setAnswers] = useState(() => ({ ...(message?.planAnswers || {}) }));
  const [additionalInput, setAdditionalInput] = useState(() => message?.planAdditionalInput || "");
  const [step, setStep] = useState(() => getFirstUnansweredPlanIndex(questions, message?.planAnswers || {}));
  const [submitting, setSubmitting] = useState(false);

  const persistProgress = (nextAnswers, nextAdditional) => {
    onProgress?.(message, nextAnswers, nextAdditional);
  };

  const safeStep = Math.min(step, Math.max(questions.length - 1, 0));
  const question = questions[safeStep];
  const isLast = safeStep >= questions.length - 1;
  const currentValue = question ? answers[question.id] : undefined;
  const canContinue = question ? isPlanQuestionAnswered(question, currentValue) : false;
  const Renderer = question ? (QUESTION_RENDERERS[question.type] || UnsupportedQuestion) : null;
  const answeredCount = countAnsweredPlanQuestions(questions, answers);

  const intro = message?.planIntro || message?.content || "Let's understand your requirements before we start building.";

  const selectedSummary = useMemo(() => {
    if (!submitted) return [];
    const rows = questions
      .map((item) => {
        const value = (message.planAnswers || answers)[item.id];
        if (Array.isArray(value)) {
          return { id: item.id, question: item.question, label: value.map((v) => getOptionLabel(item, v)).join(", ") };
        }
        const label = getOptionLabel(item, value);
        return label ? { id: item.id, question: item.question, label } : null;
      })
      .filter(Boolean);
    const extra = message.planAdditionalInput || additionalInput;
    if (extra) {
      rows.push({ id: "additionalInput", question: "Additional input", label: extra });
    }
    return rows;
  }, [additionalInput, answers, message.planAdditionalInput, message.planAnswers, questions, submitted]);

  const handleContinue = async () => {
    if (!canContinue || submitting || submitted || !question) return;
    if (!isLast) {
      setStep((prev) => Math.min(prev + 1, questions.length - 1));
      return;
    }
    if (!areAllRequiredPlanQuestionsAnswered(questions, answers)) {
      setStep(getFirstUnansweredPlanIndex(questions, answers));
      return;
    }
    if (!onContinue) return;
    setSubmitting(true);
    try {
      await onContinue(message, answers, additionalInput);
    } finally {
      setSubmitting(false);
    }
  };

  if (!question) return null;

  return (
    <div className={`chat-message assistant sa-plan-message ${submitted ? "is-submitted" : ""}`}>
      <div className="message-wrapper">
        <div className="message-header">
          <span className="assistant-name font-inter-bold">
            {message.title || "SuperAgentX AI"}
          </span>
          {message.createdAt ? (
            <span className="message-timestamp">{formatTimestamp(message.createdAt)}</span>
          ) : null}
        </div>

        <div className="sa-plan-card" role="region" aria-label="Planning questions">
          <div className="sa-plan-card-header">
            <span className="sa-plan-card-kicker">
              <i className="fa-solid fa-list-check" aria-hidden="true" />
              Planning your application
            </span>
            {questions.length > 1 && !submitted ? (
              <span className="sa-plan-card-progress">
                {answeredCount} of {questions.length} answered
              </span>
            ) : null}
          </div>

          {intro ? <p className="sa-plan-card-intro">{intro}</p> : null}

          {!submitted && questions.length > 1 && (
            <div className="sa-plan-steps" role="tablist" aria-label="Planning questions">
              {questions.map((item, index) => {
                const value = answers[item.id];
                const done = Array.isArray(value)
                  ? value.length > 0
                  : value != null && String(value).trim() !== "";
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`sa-plan-step ${index === safeStep ? "is-current" : ""} ${done ? "is-done" : ""}`}
                    onClick={() => setStep(index)}
                    disabled={submitting}
                    title={item.question}
                    aria-label={`Question ${index + 1}${done ? ", answered" : ", pending"}`}
                  >
                    {done ? <i className="fa-solid fa-check" aria-hidden="true" /> : index + 1}
                  </button>
                );
              })}
            </div>
          )}

          {!submitted && (
            <p className="sa-plan-save-hint">Progress is saved with this project. You can refresh or come back later.</p>
          )}

          {submitted ? (
            <ul className="sa-plan-summary">
              {selectedSummary.map((item) => (
                <li key={item.id}>
                  <span className="sa-plan-summary-q">{item.question}</span>
                  <span className="sa-plan-summary-a">{item.label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <h3 className="sa-plan-question">{question.question}</h3>
              {question.description && question.type !== planQuestionType.TEXT ? (
                <p className="sa-plan-question-help">{question.description}</p>
              ) : null}
              <Renderer
                question={question}
                value={currentValue ?? defaultAnswerFor(question)}
                disabled={submitting}
                onChange={(next) => {
                  const nextAnswers = { ...answers, [question.id]: next };
                  setAnswers(nextAnswers);
                  persistProgress(nextAnswers, additionalInput);
                }}
              />
              {isLast && (
                <div className="sa-plan-additional">
                  <label className="sa-plan-additional-label" htmlFor={`plan-additional-${message.id || "active"}`}>
                    Additional input
                    <span>Optional</span>
                  </label>
                  <textarea
                    id={`plan-additional-${message.id || "active"}`}
                    className="sa-plan-text-input"
                    value={additionalInput}
                    disabled={submitting}
                    onChange={(event) => {
                      const value = event.target.value;
                      setAdditionalInput(value);
                      persistProgress(answers, value);
                    }}
                    placeholder="Add extra context, constraints, or notes for this plan"
                    rows={3}
                  />
                </div>
              )}
            </>
          )}

          {!submitted && (
            <div className="sa-plan-card-actions">
              {safeStep > 0 ? (
                <button
                  type="button"
                  className="sa-plan-back-btn"
                  onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                  disabled={submitting}
                >
                  Back
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="sa-plan-continue-btn"
                onClick={handleContinue}
                disabled={!canContinue || submitting}
              >
                {submitting ? "Sending…" : isLast ? "Continue plan" : "Continue"}
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanQuestionCard;
